/**
 * REST API klient pro BMS. Tenké axios wrappery.
 */

import { deleteAsync, getAsync, patchAsync, postAsync } from "./axios";
import type {
  BmsChemistry,
  BmsDeviceInfo,
  BmsDiscoveredBle,
  BmsDiscoveredSerial,
  BmsListItem,
  BmsRecord,
  BmsRuntime,
  BmsTransportType,
} from "@client/types/bms";
import type { WriteBatchResult } from "@client/types/verify";

export const getBmsList = () => getAsync<ApiResp<BmsListItem[]>>("/api/bms");

export const getBmsSnapshot = (slug: string) =>
  getAsync<ApiResp<{ record: BmsRecord; runtime: BmsRuntime | null }>>(`/api/bms/${slug}/snapshot`);

export const postBmsScan = (timeoutMs: number = 10_000) =>
  postAsync<ApiResp<BmsDiscoveredBle[]>>("/api/bms/scan", { timeoutMs });

export const getSerialPorts = () => getAsync<ApiResp<BmsDiscoveredSerial[]>>("/api/bms/serial-ports");

export interface BmsDebugFrame {
  length: number;
  hex: string;
  timestamp: number;
}
export const getBmsDebugFrame = (slug: string) => getAsync<ApiResp<BmsDebugFrame>>(`/api/bms/${slug}/debug`);

export interface ProbePayload {
  transportType: BmsTransportType;
  transportTarget: string;
  timeoutMs?: number;
}
export const postBmsProbe = (data: ProbePayload) => postAsync<ApiResp<BmsDeviceInfo>>("/api/bms/probe", data);

export interface CreateBmsPayload {
  slug: string;
  name: string;
  transportType: BmsTransportType;
  transportTarget: string;
  cellCount?: number;
  capacityMah?: number | null;
  chemistry?: BmsChemistry;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const postBms = (data: CreateBmsPayload) => postAsync<ApiResp<BmsRecord>>("/api/bms", data);

export interface UpdateBmsPayload {
  name?: string;
  transportType?: BmsTransportType;
  transportTarget?: string;
  cellCount?: number;
  capacityMah?: number | null;
  chemistry?: BmsChemistry;
  notes?: string | null;
  enabled?: boolean;
  order?: number | null;
}

export const patchBms = (slug: string, data: UpdateBmsPayload) =>
  patchAsync<ApiResp<BmsRecord>>(`/api/bms/${slug}`, data);

export const deleteBmsReq = (slug: string) => deleteAsync<ApiResp<BmsRecord>>(`/api/bms/${slug}`);

export const postBmsReconnect = (slug: string) => postAsync<ApiResp<boolean>>(`/api/bms/${slug}/reconnect`);

export type ControlChannel = "charge" | "discharge" | "balance";

export const postBmsControl = (slug: string, channel: ControlChannel, on: boolean) =>
  postAsync<ApiResp<boolean>>(`/api/bms/${slug}/control/${channel}`, { on });

export interface BmsSettingsPatch {
  cellOvpMv?: number;
  cellOvrMv?: number;
  cellUvpMv?: number;
  cellUvrMv?: number;
  balanceTriggerDeltaMv?: number;
  balanceStartMv?: number;
  capacityMah?: number;
  chargeOcpA?: number;
  dischargeOcpA?: number;
}

export const patchBmsSettings = (slug: string, data: BmsSettingsPatch) =>
  patchAsync<ApiResp<WriteBatchResult>>(`/api/bms/${slug}/settings`, data);
